import { apiErrorMessage } from '@/api/client';
import { EnumEditor } from '@/components/admin/EnumEditor';
import { RegexTester, SAMPLE_TEXT } from '@/components/admin/RegexTester';
import { GHOST_BUTTON, INPUT, LARGE_MODAL, primaryClasses, segClasses, slugOf } from '@/components/admin/ui';
import { Modal } from '@/components/common/Modal';
import { ITEM_TYPES, type CategoryModel, type CreateItem, type ItemType } from '@/types/categories.types';
import type { FC } from 'react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface NewItemModalProps {
  categories: CategoryModel[];
  /** Identifiers already taken, for the inline duplicate check (the backend answers 409 anyway). */
  existingIds: ReadonlySet<string>;
  onCreate: (body: CreateItem) => Promise<void>;
  onClose: () => void;
}

const ID_PATTERN = /^[a-z0-9_]+$/;
const LABEL = 'text-[11.5px] font-semibold text-muted';

/**
 * The prototype's "Nová položka" dialog: description with the identifier suggested from it (until
 * the identifier is typed by hand), category toggles, the type switch, and the configuration —
 * a prompt, a regex with the tester, or the ENUM editor.
 */
export const NewItemModal: FC<NewItemModalProps> = ({ categories, existingIds, onCreate, onClose }) => {
  const { t } = useTranslation();
  const titleId = useId();
  const [name, setName] = useState('');
  const [id, setId] = useState('');
  const [idTouched, setIdTouched] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [type, setType] = useState<ItemType>('LLM');
  const [config, setConfig] = useState('');
  const [values, setValues] = useState<string[]>([]);
  const [sample, setSample] = useState(SAMPLE_TEXT);
  const [isSaving, setIsSaving] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const idTrim = id.trim();
  const duplicate = !!idTrim && existingIds.has(idTrim);
  const badId = !!idTrim && !ID_PATTERN.test(idTrim);
  const cleanValues = values.map((value) => value.trim()).filter(Boolean);
  const hasConfig = type === 'ENUM' ? cleanValues.length > 0 : config.trim().length > 0;
  const ok = !!name.trim() && !!idTrim && !duplicate && !badId && hasConfig;

  const hint = serverError
    ? serverError
    : duplicate
      ? t('admin.newItem.hintDup')
      : badId
        ? t('admin.newItem.hintBadId')
        : !ok
          ? t('admin.newItem.hintIncomplete')
          : selected.length
            ? ''
            : t('admin.newItem.hintNoCategory');

  const save = async () => {
    if (!ok) return;
    setIsSaving(true);
    setServerError(null);
    try {
      await onCreate({
        id: idTrim,
        name: name.trim(),
        type,
        categories: selected,
        ...(type === 'ENUM' ? { values: cleanValues } : { config: config.trim() }),
      });
      onClose();
    } catch (err) {
      setServerError(apiErrorMessage(err, t('admin.saveFailed')));
      setIsSaving(false);
    }
  };

  const configLabel = t(`admin.editor.kind.${type}`);

  return (
    <Modal onClose={onClose} maxWidth={920} labelledBy={titleId} className={LARGE_MODAL}>
      <div className="border-b border-line px-[22px] pb-3.5 pt-[18px]">
        <div id={titleId} className="text-base font-bold text-primary">
          {t('admin.newItem.title')}
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-3.5 overflow-auto px-[22px] py-4">
        <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-3">
          <label className="flex flex-col gap-[5px]">
            <span className={LABEL}>{t('admin.newItem.name')}</span>
            <input
              value={name}
              onChange={(event) => {
                setName(event.target.value);
                if (!idTouched) setId(slugOf(event.target.value));
              }}
              placeholder={t('admin.newItem.namePlaceholder')}
              // biome-ignore lint/a11y/noAutofocus: the dialog starts with this field
              autoFocus={true}
              className={`${INPUT} w-full px-2.5 py-2 text-[12.5px]`}
            />
          </label>
          <label className="flex flex-col gap-[5px]">
            <span className={LABEL}>{t('admin.newItem.id')}</span>
            <input
              value={id}
              onChange={(event) => {
                setIdTouched(true);
                setId(event.target.value);
              }}
              placeholder={t('admin.newItem.idPlaceholder')}
              aria-invalid={duplicate || badId}
              className={`${INPUT} w-full px-2.5 py-2 font-mono text-xs`}
              style={duplicate || badId ? { borderColor: '#c0102c' } : undefined}
            />
          </label>
        </div>

        <div className="flex flex-col gap-1.5">
          <span className={LABEL}>{t('admin.newItem.categories')}</span>
          <div className="flex flex-wrap gap-1.5">
            {categories.map((category) => {
              const on = selected.includes(category.key);
              return (
                <button
                  key={category.key}
                  type="button"
                  aria-pressed={on}
                  onClick={() =>
                    setSelected(on ? selected.filter((key) => key !== category.key) : [...selected, category.key])
                  }
                  className={`flex cursor-pointer items-center gap-1.5 rounded border px-[9px] py-[5px] text-[11.5px] text-ink ${
                    on ? 'font-semibold' : 'font-normal'
                  }`}
                  style={{ background: on ? category.tint : '#fff', borderColor: on ? category.color : '#dbe5ee' }}
                >
                  <span className="inline-block h-2 w-2 flex-none" style={{ background: category.color }} />
                  {category.label_cs}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <span className={LABEL}>{t('admin.newItem.type')}</span>
          <div className="flex self-start overflow-hidden rounded border border-control" role="radiogroup">
            {ITEM_TYPES.map((value) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={type === value}
                onClick={() => setType(value)}
                className={segClasses(type === value)}
              >
                {t(`admin.types.${value}`)}
              </button>
            ))}
          </div>
        </div>

        <div className={`flex flex-col gap-[5px] ${type === 'regex' ? 'flex-none' : 'min-h-40 flex-1'}`}>
          <span className={LABEL}>{configLabel}</span>
          {type === 'ENUM' ? (
            <EnumEditor values={values} onChange={setValues} />
          ) : (
            <textarea
              value={config}
              onChange={(event) => setConfig(event.target.value)}
              placeholder={t(`admin.newItem.configPlaceholder.${type}`)}
              aria-label={configLabel}
              rows={5}
              className={`w-full resize-y rounded border border-control bg-wash px-3 py-2.5 leading-[1.6] text-ink focus:border-primary focus:outline-none ${
                type === 'regex'
                  ? 'min-h-[70px] flex-none font-mono text-xs'
                  : 'min-h-40 flex-1 font-[inherit] text-[12.5px]'
              }`}
            />
          )}
        </div>

        {type === 'regex' ? (
          <div className="border-t border-hairline pt-3.5">
            <RegexTester pattern={config} sample={sample} onSampleChange={setSample} />
          </div>
        ) : null}
      </div>

      <div className="flex items-center gap-2 border-t border-hairline px-[22px] pb-[18px] pt-3">
        <span className={`flex-1 text-[11.5px] ${serverError ? 'text-danger' : 'text-label-2'}`} aria-live="polite">
          {hint}
        </span>
        <button type="button" onClick={onClose} className={GHOST_BUTTON}>
          {t('admin.newItem.cancel')}
        </button>
        <button type="button" onClick={save} disabled={!ok || isSaving} className={primaryClasses(ok)}>
          {t('admin.newItem.submit')}
        </button>
      </div>
    </Modal>
  );
};
